import { useEffect, useMemo, useState } from "react";
import { FiX, FiSend } from "react-icons/fi";

/**
 * Confirm image/video before sending in admin chats.
 */
export default function MediaSendConfirmModal({
  open,
  file,
  sending = false,
  onCancel,
  onConfirm,
  title,
}) {
  const [objectUrl, setObjectUrl] = useState(null);

  const kind = useMemo(() => {
    const t = String(file?.type || "").toLowerCase();
    if (t.startsWith("video/")) return "video";
    if (t.startsWith("image/")) return "image";
    return null;
  }, [file]);

  useEffect(() => {
    if (!file || !kind) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, kind]);

  if (!open || !file || !kind || !objectUrl) return null;

  const heading =
    title || (kind === "video" ? "Send this video?" : "Send this photo?");

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-send-confirm-title"
      onClick={sending ? undefined : onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 id="media-send-confirm-title" className="text-sm font-semibold text-gray-900">
            {heading}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Cancel"
          >
            <FiX size={18} />
          </button>
        </div>
        <div className="bg-gray-50 flex items-center justify-center min-h-[220px] max-h-[50vh] p-3">
          {kind === "video" ? (
            <video
              src={objectUrl}
              controls
              className="max-w-full max-h-[46vh] rounded-lg"
            />
          ) : (
            <img
              src={objectUrl}
              alt="Preview"
              className="max-w-full max-h-[46vh] object-contain rounded-lg"
            />
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={sending}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-[#E52732] text-white hover:bg-[#c41f29] disabled:opacity-60 min-w-[96px] justify-center"
          >
            {sending ? (
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <>
                <FiSend size={14} />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
